import React from "react"
import {
  CustomReasonCode,
  Maybe,
  Order,
  OrderModificationType
} from "lib/types/generated/graphql-types"
import Refund from "../../../../../components/Order/Updates/Refund"
import { ReactComponent as RefundIcon } from "images/icons/arrows-repeat.svg"
import { ReactComponent as DeliverIcon } from "../../../../../images/icons/truck-ramp-box.svg"
import { ReactComponent as CancelIcon } from "../../../../../images/icons/ban.svg"
import { ReactComponent as ReleaseIcon } from "images/icons/cart-arrow-up.svg"
import { ReactComponent as ModifyIcon } from "images/icons/sliders.svg"
import { ReactComponent as CompensationIcon } from "images/icons/hand-holding-dollar.svg"
import { ReactComponent as ModifyAddressIcon } from "images/icons/address-card.svg"
import { Container, Info } from "./Updates.styled"
import Delivery from "../../../../../components/Order/Updates/Delivery"
import Cancellation from "../../../../../components/Order/Updates/Cancellation"
import Compensation from "../../../../../components/Order/Updates/Compensation"
import Release from "../../../../../components/Order/Updates/Release"
import ModificationOrderLines from "../../../../../components/Order/Updates/ModificationOrderLines"
import { useQuery } from "@apollo/client"
import ALL_REASON_CODES from "../../../../../graphql/queries/settings/reasonCodes/AllReasonCodes"
import ModificationShippingFees from "../../../../../components/Order/Updates/ModificationShippingFees"
import { Compensation as CompensationClass } from "classes/order/Compensation"
import ModificationOrderAddresses from "../../../../../components/Order/Updates/ModificationOrderAddresses"
import { isOrderGiftCardProductRetain24 } from "../../../../../components/Order/helpers"

type Props = {
  order: Order
}

const Updates = ({ order }: Props) => {
  const { data } = useQuery(ALL_REASON_CODES)
  const customReasons = data?.Order?.customReasonCodes?.codes

  const getCustomCode = (code: Maybe<string> | undefined) =>
    customReasons?.find((customCode: CustomReasonCode) => customCode.code === code)

  const {
    Cancellations,
    Refunds,
    Deliveries,
    Releases,
    Compensations,
    Modifications,
    orderLines,
    gifts,
    currencyCode,
    shippingFees,
    GiftCardProduct
  } = order

  const modificationOrderLines = Modifications.filter(
    (modification) => modification.type === OrderModificationType.OrderLines
  )
  const modificationShippingFees = Modifications.filter(
    (modification) => modification.type === OrderModificationType.ShippingFees
  )

  const modificationAddresses = Modifications.filter(
    (modification) => modification.type === OrderModificationType.OrderAddresses
  )

  const compensationClass = new CompensationClass(Compensations)

  return (
    <>
      {Cancellations.length > 0 && (
        <Container>
          <Info>
            <CancelIcon />
            <h2>Cancellation</h2>
          </Info>
          {Cancellations?.map((cancellation) => (
            <Cancellation
              key={cancellation.id}
              cancellationId={cancellation.id}
              getCustomCode={getCustomCode}
            />
          ))}
        </Container>
      )}
      {modificationOrderLines.length > 0 && (
        <Container>
          <Info>
            <ModifyIcon />
            <h2>Order line modifications</h2>
          </Info>
          {modificationOrderLines?.map((modification) => (
            <ModificationOrderLines
              key={modification.id}
              modificationId={modification.id}
              currencyCode={order.currencyCode}
              orderLines={order?.orderLines ?? []}
              getCustomCode={getCustomCode}
            />
          ))}
        </Container>
      )}
      {modificationShippingFees.length > 0 && (
        <Container>
          <Info>
            <ModifyIcon />
            <h2>Shipping fee modifications</h2>
          </Info>
          {modificationShippingFees?.map((modification) => (
            <ModificationShippingFees
              key={modification.id}
              modificationId={modification.id}
              currencyCode={order.currencyCode}
              shippingFees={order?.shippingFees ?? []}
              getCustomCode={getCustomCode}
            />
          ))}
        </Container>
      )}
      {modificationAddresses.length > 0 && (
        <Container>
          <Info>
            <ModifyAddressIcon />
            <h2>Address modifications</h2>
          </Info>
          {modificationAddresses?.map((modification) => (
            <ModificationOrderAddresses
              key={modification.id}
              modificationId={modification.id}
              getCustomCode={getCustomCode}
            />
          ))}
        </Container>
      )}
      {Deliveries.length > 0 && (
        <Container>
          <Info>
            <DeliverIcon />
            <h2>Deliveries</h2>
          </Info>
          {Deliveries?.map((delivery) => (
            <Delivery
              key={delivery.id}
              deliveryId={delivery.id}
              currencyCode={currencyCode}
              orderLines={orderLines ?? []}
              gifts={gifts ?? []}
              shippingFees={shippingFees ?? []}
              getCustomCode={getCustomCode}
              giftCardProducts={
                isOrderGiftCardProductRetain24(GiftCardProduct)
                  ? GiftCardProduct.giftCardProducts
                  : []
              }
            />
          ))}
        </Container>
      )}
      {Releases.length > 0 && (
        <Container>
          <Info>
            <ReleaseIcon />
            <h2>Releases</h2>
          </Info>
          {Releases?.map((release) => (
            <Release
              key={release.id}
              releaseId={release.id}
              currencyCode={currencyCode}
              orderLines={orderLines ?? []}
              gifts={gifts ?? []}
              shippingFees={shippingFees ?? []}
              getCustomCode={getCustomCode}
            />
          ))}
        </Container>
      )}
      {Compensations.length > 0 && (
        <Container>
          <Info>
            <CompensationIcon />
            <h2>Compensations</h2>
          </Info>
          {Compensations?.map((compensation) => (
            <Compensation
              key={compensation.id}
              compensationId={compensation.id}
              shippingFees={shippingFees ?? []}
              currencyCode={currencyCode}
              orderLines={orderLines ?? []}
              getCustomCode={getCustomCode}
              compensationClass={compensationClass}
              isTaxIncludedInPrice={order.isTaxIncludedInPrice}
            />
          ))}
        </Container>
      )}
      {Refunds.length > 0 && (
        <Container>
          <Info>
            <RefundIcon />
            <h2>Refunds</h2>
          </Info>
          {Refunds?.map((refund) => (
            <Refund
              key={refund.id}
              refundId={refund.id}
              shippingFees={shippingFees ?? []}
              currencyCode={currencyCode}
              orderLines={orderLines ?? []}
              gifts={gifts ?? []}
              getCustomCode={getCustomCode}
            />
          ))}
        </Container>
      )}
    </>
  )
}
export default Updates
